import { useState } from 'react';
import { Shield, Lock, Scale, CheckCircle2, Info } from 'lucide-react';

interface TrustBadge {
    icon: React.ReactNode;
    title: string;
    description: string;
    color: string;
}

const badges: TrustBadge[] = [
    {
        icon: <Scale className="w-5 h-5" />,
        title: 'Validade Jurídica',
        description: 'Assinatura eletrônica válida conforme Lei 14.063/2020 (Assinatura Eletrônica Avançada)',
        color: 'emerald',
    },
    {
        icon: <Shield className="w-5 h-5" />,
        title: 'LGPD Compliant',
        description: 'Seus dados são protegidos conforme a Lei Geral de Proteção de Dados (Lei 13.709/2018)',
        color: 'blue',
    },
    {
        icon: <Lock className="w-5 h-5" />,
        title: 'Conexão Segura',
        description: 'Comunicação criptografada com certificado SSL/TLS de 256 bits',
        color: 'amber',
    },
];

interface TrustBadgesProps {
    className?: string;
    variant?: 'horizontal' | 'vertical';
    showTooltips?: boolean;
}

export const TrustBadges: React.FC<TrustBadgesProps> = ({
    className = '',
    variant = 'horizontal',
    showTooltips = true,
}) => {
    const [activeBadge, setActiveBadge] = useState<number | null>(null);

    const colorClasses = {
        emerald: {
            bg: 'bg-emerald-500/10',
            border: 'border-emerald-500/30',
            icon: 'text-emerald-400',
            text: 'text-emerald-300',
        },
        blue: {
            bg: 'bg-blue-500/10',
            border: 'border-blue-500/30',
            icon: 'text-blue-400',
            text: 'text-blue-300',
        },
        amber: {
            bg: 'bg-amber-500/10',
            border: 'border-amber-500/30',
            icon: 'text-amber-400',
            text: 'text-amber-300',
        },
    };

    return (
        <div className={`${className}`}>
            {/* Header */}
            <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <h3 className="font-semibold text-white">Assinatura Segura e Válida</h3>
            </div>

            {/* Badges Grid */}
            <div
                className={`grid gap-3 ${variant === 'horizontal'
                    ? 'grid-cols-1 sm:grid-cols-3'
                    : 'grid-cols-1'
                    }`}
            >
                {badges.map((badge, index) => {
                    const colors = colorClasses[badge.color as keyof typeof colorClasses];

                    return (
                        <div
                            key={index}
                            className={`relative ${colors.bg} ${colors.border} border rounded-xl p-4 cursor-pointer transition-all hover:scale-[1.02]`}
                            onMouseEnter={() => showTooltips && setActiveBadge(index)}
                            onMouseLeave={() => setActiveBadge(null)}
                            onClick={() => setActiveBadge(activeBadge === index ? null : index)}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`${colors.icon}`}>{badge.icon}</div>
                                <div>
                                    <p className={`font-medium ${colors.text} text-sm`}>
                                        {badge.title}
                                    </p>
                                </div>
                                {showTooltips && (
                                    <Info className="w-4 h-4 text-white/30 ml-auto" />
                                )}
                            </div>

                            {/* Tooltip */}
                            {showTooltips && activeBadge === index && (
                                <div className="absolute z-10 left-0 right-0 top-full mt-2 p-3 bg-slate-800 border border-slate-700 rounded-lg shadow-xl">
                                    <p className="text-sm text-white/80">{badge.description}</p>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Footer legal text */}
            <p className="mt-4 text-xs text-white/40 text-center">
                Este documento tem validade jurídica equivalente a um contrato físico assinado
            </p>
        </div>
    );
};
